import type { BatchListItem } from '@cartwhl/common/api/getBatches';
import type { Project, Scene } from '@cartwhl/common/types';
import { ProjectType } from '@cartwhl/common/types';
import { getStudioUrl } from '@cartwhl/common/utils';

import { getMotionEditorScenePath } from '../MotionEditor/public';
import type {
  DashboardWorkItem,
  DashboardWorkItemScope,
  DashboardWorkSection,
} from './types';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;

export function toSceneWorkItem(
  scene: Scene,
  scope: DashboardWorkItemScope,
): DashboardWorkItem {
  const updatedAt = scene.lastUpdatedAt ?? scene.createdAt;
  const thumbnailSrc =
    scene.thumbnailURL?.trim() || scene.sceneThumbnailURL?.trim() || undefined;

  return {
    id: scene.sceneID,
    kind: 'scene',
    scope,
    title: scene.sceneName || 'Untitled Scene',
    href: getMotionEditorScenePath(scene.sceneID),
    thumbnailSrc,
    createdByUser: scene.createdByUser?.trim() || undefined,
    kindLabel: 'Cartwheel Scene',
    createdAt: scene.createdAt,
    updatedAt,
  };
}

export function toLegacyProjectWorkItem(
  project: Project,
  scope: DashboardWorkItemScope,
): DashboardWorkItem {
  const updatedAt = project.lastUpdatedAt ?? project.createdAt;
  const projectPath = project.type === ProjectType.POSE ? 'pose' : 'motion';

  return {
    id: `legacy-project:${project.projectID}`,
    kind: 'canvas-project',
    scope,
    title: project.projectName || 'Untitled Scene',
    href: `${getStudioUrl()}/${projectPath}/${project.projectID}`,
    external: true,
    thumbnailSrc: project.projectThumbnailURL,
    createdByUser: project.createdBy?.trim() || undefined,
    kindLabel: 'Canvas Project',
    createdAt: project.createdAt,
    updatedAt,
  };
}

export function toBatchWorkItem(
  batch: BatchListItem,
  scope: DashboardWorkItemScope,
): DashboardWorkItem | null {
  const updatedAt = batch.lastUpdatedAt ?? batch.createdAt;
  const batchType = batch.type.toUpperCase();
  const batchModel = batch.model?.toUpperCase();
  const thumbnailSrc = batch.thumbnailURL?.trim() || undefined;
  const createdByUser = batch.createdByUser?.trim() || undefined;

  if (batchType === 'COMIC') {
    return {
      id: batch.batchID,
      kind: 'comic-job',
      scope,
      title: batch.batchName || 'Untitled Comic job',
      href: `/comic/${batch.batchID}`,
      thumbnailSrc,
      createdByUser,
      kindLabel: 'Comic job',
      createdAt: batch.createdAt,
      updatedAt,
      itemCount: batch.numTotalJobs,
      batchStatus: batch.status,
    };
  }

  if (batchType === 'GENERATION' && batchModel !== 'SCOOT') {
    return {
      id: batch.batchID,
      kind: 'swing-job',
      scope,
      title: batch.batchName || 'Untitled Swing job',
      href: `/generation/${batch.batchID}`,
      thumbnailSrc,
      createdByUser,
      kindLabel: 'Swing job',
      createdAt: batch.createdAt,
      updatedAt,
      itemCount: batch.numTotalJobs,
      batchStatus: batch.status,
    };
  }

  if (batchType === 'MASCOT') {
    // Discontinued: existing outputs remain available in Library, outside Dashboard.
    return null;
  }

  if (batchType === 'CHARACTER') {
    return {
      id: batch.batchID,
      kind: 'character-job',
      scope,
      title: batch.batchName || 'Untitled Character job',
      href: `/character/${batch.batchID}`,
      thumbnailSrc,
      createdByUser,
      kindLabel: 'Character batch',
      createdAt: batch.createdAt,
      updatedAt,
      itemCount: batch.numTotalJobs,
      batchStatus: batch.status,
    };
  }

  return null;
}

export function groupDashboardWorkItems(
  items: DashboardWorkItem[],
): DashboardWorkSection[] {
  const sortedItems = [...items].sort(
    (a, b) => getDateTime(b.updatedAt) - getDateTime(a.updatedAt),
  );
  const groupedSections = new Map<string, DashboardWorkSection>();

  sortedItems.forEach((item) => {
    const section = getDateSection(item.updatedAt);
    const existingSection = groupedSections.get(section.id);

    if (existingSection) {
      existingSection.items.push(item);
    } else {
      groupedSections.set(section.id, {
        id: section.id,
        title: section.title,
        items: [item],
      });
    }
  });

  return [...groupedSections.values()];
}

export function formatEditedAt(dateValue: string): string {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return 'recently';
  }

  const now = new Date();
  const deltaMs = date.getTime() - now.getTime();
  const absoluteDeltaMs = Math.abs(deltaMs);
  const relativeTimeFormat = new Intl.RelativeTimeFormat('en', {
    numeric: 'auto',
  });

  if (absoluteDeltaMs < DAY_MS) {
    return relativeTimeFormat.format(Math.round(deltaMs / DAY_MS), 'day');
  }
  if (absoluteDeltaMs < 30 * DAY_MS) {
    return relativeTimeFormat.format(Math.round(deltaMs / DAY_MS), 'day');
  }
  if (absoluteDeltaMs < 12 * MONTH_MS) {
    return relativeTimeFormat.format(Math.round(deltaMs / MONTH_MS), 'month');
  }

  return relativeTimeFormat.format(
    Math.round(deltaMs / (365 * DAY_MS)),
    'year',
  );
}

function getDateSection(dateValue: string): { id: string; title: string } {
  const date = new Date(dateValue);
  const now = new Date();

  if (Number.isNaN(date.getTime())) {
    return { id: 'older', title: 'Older work' };
  }

  const ageMs = now.getTime() - date.getTime();

  if (ageMs <= WEEK_MS) {
    return { id: 'created-this-week', title: 'Created this week' };
  }

  if (ageMs <= MONTH_MS) {
    return { id: 'created-this-month', title: 'Created this month' };
  }

  return {
    id: `month-${date.getFullYear()}-${date.getMonth()}`,
    title: new Intl.DateTimeFormat('en', {
      month: 'long',
      year: 'numeric',
    }).format(date),
  };
}

function getDateTime(dateValue: string): number {
  const dateTime = new Date(dateValue).getTime();
  return Number.isNaN(dateTime) ? 0 : dateTime;
}
